import { ReactNode } from "react";
import { Button, Divider, MenuItem, SxProps, Theme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link as RouterLink } from "react-router-dom";
import { AnchoredMenu } from "./AnchoredMenu";

// Shared manage-page row-action menu (platform UI/UX polish, Phase A).
// NO Phase A consumer — ManageMembers keeps its two-button Actions cell.
// Defaults mirror ManageEvents' existing page-level menu (the declarative
// actionsByStatus matrix pattern); ManageInvoices / ManageEvents /
// ManageEmails adopt in Phase B. Usage pattern: a SINGLE page-level instance
// anchored via state { row, anchorEl } — NOT one Menu per row.

export type RowActionDescriptor<ActionKey extends string = string> = {
  key: ActionKey;
  label: string;
  // Navigation actions render as real links so standard browser actions such
  // as right-click, Cmd/Ctrl-click, and middle-click remain available.
  to?: string;
  // rendered before the label. Only Manage Invoices uses icons: the Manage
  // Events and Manage Emails designs are label-only (EV-40).
  icon?: ReactNode;
  // red item styling
  destructive?: boolean;
  // semibold label (Claude Design Manage Events "Delete Event")
  strong?: boolean;
  // a <Divider/> is emitted immediately before this item (suppressed when
  // the item ends up first after hidden items are filtered out)
  dividerBefore?: boolean;
  disabled?: boolean;
};

// Exactly ManageEvents' actionsByStatus shape.
export type RowActionMatrix<Status extends string, ActionKey extends string> =
  Readonly<Record<Status, ReadonlyArray<ActionKey>>>;

export type BuildRowActionsConfig<
  Status extends string,
  ActionKey extends string,
  Row,
> = {
  matrix: RowActionMatrix<Status, ActionKey>;
  labels: Readonly<Record<ActionKey, string>>;
  // Events: deriveStatus
  getStatus: (row: Row) => Status;
  // Events: dangerActions
  destructiveActions?: ReadonlySet<ActionKey>;
  // Events: new Set(["delete"])
  dividerBeforeActions?: ReadonlySet<ActionKey>;
  getIcon?: (action: ActionKey, row: Row) => ReactNode;
  // Invoices: actionLoading / !canUseAction gates
  isDisabled?: (action: ActionKey, row: Row) => boolean;
  // Emails: viewAnalytics hidden for drafts; Invoices front-end predicates
  isHidden?: (action: ActionKey, row: Row) => boolean;
};

// Hidden items are filtered OUT before descriptors are built, so divider
// placement is recomputed against the visible list.
export function buildRowActions<
  Status extends string,
  ActionKey extends string,
  Row,
>(
  config: BuildRowActionsConfig<Status, ActionKey, Row>
): (row: Row) => RowActionDescriptor<ActionKey>[] {
  return (row) => {
    const status = config.getStatus(row);
    const visibleActions = config.matrix[status].filter(
      (action) => !config.isHidden?.(action, row)
    );

    return visibleActions.map((action) => ({
      key: action,
      label: config.labels[action],
      icon: config.getIcon?.(action, row),
      destructive: config.destructiveActions?.has(action) || false,
      dividerBefore: config.dividerBeforeActions?.has(action) || false,
      disabled: config.isDisabled?.(action, row) || false,
    }));
  };
}

// Defaults follow the Claude Design Manage Events menu (Manage Events
// Screen.dc.html ~169-175 and ~465-467): label-only 14px items, red
// #F0202E destructive items and a 1px #ECECEC divider inset 4px 6px.
export const rowActionMenuPaperSx = {
  border: "1px solid #E0E0E0",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  minWidth: 230,
  p: "6px",
  "& .MuiList-root": { p: 0 },
};

export const rowActionMenuItemSx = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  color: "#0A0A0A",
  gap: "10px",
  borderRadius: "6px",
  minHeight: 0,
  px: "12px",
  py: "10px",
  "&:hover": { backgroundColor: "#F7F7F8" },
};

export const rowActionDestructiveItemSx = {
  color: "#F0202E",
};

// "&&&" outranks MUI's MenuItem + Divider spacing rule.
export const rowActionDividerSx = {
  "&&&": { borderColor: "#ECECEC", my: "4px", mx: "6px" },
};

const toSxArray = (sx: SxProps<Theme> | undefined) =>
  sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];

export type RowActionMenuProps<ActionKey extends string = string> = {
  actions: ReadonlyArray<RowActionDescriptor<ActionKey>>;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  // Command actions call onClose() first and then onAction. Route actions use
  // their link destination and only close this menu.
  onAction: (key: ActionKey) => void;
  paperSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
  destructiveItemSx?: SxProps<Theme>;
  dividerSx?: SxProps<Theme>;
};

// Non-modal (IF10 G1, audit T-1): Claude Design InvoiceActionsMenu.jsx
// opens a fixed popup below the trigger, right-aligned, 6px gap, with no
// backdrop. The page keeps scrolling; the popup follows the trigger and
// closes when the trigger leaves the visible area, on an outside click, on
// Esc (focus returns to the trigger) and on Tab.
// Children are a FLAT array of MenuItem/Divider elements — no fragments or
// wrapper divs, or MenuList keyboard navigation breaks.
export function RowActionMenu<ActionKey extends string = string>({
  actions,
  anchorEl,
  open,
  onClose,
  onAction,
  paperSx = rowActionMenuPaperSx,
  itemSx = rowActionMenuItemSx,
  destructiveItemSx = rowActionDestructiveItemSx,
  dividerSx = rowActionDividerSx,
}: RowActionMenuProps<ActionKey>) {
  const closeAndFocusTrigger = () => {
    anchorEl?.focus({ preventScroll: true });
    onClose();
  };

  return (
    <AnchoredMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      placement="bottom-end"
      offset={6}
      wireAnchorAria
      PaperProps={{ sx: paperSx }}
    >
      {actions.flatMap((action, index) => {
        const actionItemSx = [
          ...toSxArray(itemSx),
          ...(action.destructive ? toSxArray(destructiveItemSx) : []),
          ...(action.strong ? [{ fontWeight: 600 }] : []),
        ] as SxProps<Theme>;
        const menuItem = action.to ? (
          <MenuItem
            key={action.key}
            component={RouterLink}
            to={action.to}
            disabled={action.disabled}
            onClick={closeAndFocusTrigger}
            sx={actionItemSx}
          >
            {action.icon}
            {action.label}
          </MenuItem>
        ) : (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              closeAndFocusTrigger();
              onAction(action.key);
            }}
            sx={actionItemSx}
          >
            {action.icon}
            {action.label}
          </MenuItem>
        );

        return action.dividerBefore && index > 0
          ? [<Divider key={`${action.key}-divider`} sx={dividerSx} />, menuItem]
          : [menuItem];
      })}
    </AnchoredMenu>
  );
}

// Convenience "Actions ⌄" trigger; every Phase B page overrides sx to its
// exact existing style.
export type RowActionsTriggerProps = {
  onOpen: (anchorEl: HTMLElement) => void;
  label?: string;
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  endIcon?: ReactNode;
};

export const RowActionsTrigger = ({
  onOpen,
  label = "Actions",
  ariaLabel,
  sx,
  endIcon = <KeyboardArrowDownIcon />,
}: RowActionsTriggerProps) => (
  <Button
    type="button"
    aria-label={ariaLabel ?? label}
    aria-haspopup="menu"
    aria-expanded="false"
    variant="outlined"
    endIcon={endIcon}
    onClick={(event) => onOpen(event.currentTarget)}
    sx={sx}
  >
    {label}
  </Button>
);
